import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import { setSystemScopeIdSync } from './globalFilterSlice'
import { selectSystemScopeIdContainer } from './globalFilterSelectors'

const SYSTEM_SCOPE_ID_QUERY_PARAM = 'systemScope'

/**
 * Headless component that can be added to React markup to enable
 * URL change detection and handling of global filter selection.
 */
const GlobalFilterQueryStringHandler = () => {
  const dispatch = useAppDispatch()
  const { search } = useLocation()
  const navigate = useNavigate()

  const currentSystemScopeIdRaw = useAppSelector(
    selectSystemScopeIdContainer
  ).id

  /**
   * NOTE:
   * Workaround issue where mocks may return `string` `id`s.
   * This prevent the `string` -> `number` correction dispatch
   * which cause unnecessary rerenders.
   */
  const currentSystemScopeId = currentSystemScopeIdRaw as unknown as
    | string
    | undefined

  // Handle URL changes
  useEffect(() => {
    const params = new URLSearchParams(search)
    const systemScopeParamValue = params.get(SYSTEM_SCOPE_ID_QUERY_PARAM)

    // if URL contains global filter query string
    if (systemScopeParamValue != null && systemScopeParamValue.length > 0) {
      // if query string do not match active filter in global filter state
      const systemScopeParamId = systemScopeParamValue

      if (systemScopeParamId !== currentSystemScopeId) {
        // update filter in state
        dispatch(setSystemScopeIdSync(systemScopeParamId))
      }
    } else {
      // if URL do not include filter string but it exist in store
      if (currentSystemScopeId != null) {
        // Unset profile instead of rewritting URL to include it
        dispatch(setSystemScopeIdSync(undefined))
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  // Handle state changes
  useEffect(() => {
    const params = new URLSearchParams(search)
    const systemScopeParamValue = params.get(SYSTEM_SCOPE_ID_QUERY_PARAM)

    // filter in state changed = intentional change from UI
    if (currentSystemScopeId != null) {
      const systemScopeParamId = systemScopeParamValue

      // if state is different than what is already in URL
      if (systemScopeParamId !== currentSystemScopeId) {
        // Update URL to reflect new seleted state
        params.set(SYSTEM_SCOPE_ID_QUERY_PARAM, currentSystemScopeId.toString())

        navigate({
          search: params.toString()
        })
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSystemScopeId])

  return <></>
}

export default GlobalFilterQueryStringHandler
